import axios from '@/clientAPI.ts'
import * as types from '../types'
import {
    Channel,
    ListChannelsResponse,
    ListMessagesResponse,
    ChannelsTypesE,
    SendMessageRequest,
    CreateBroadcastPayload,
    Broadcast,
    CreateGroupPayload,
    SendBroadcastMessageSchema,
    ChatMessageList,
    GetGroupsResponse
} from '../chat.model'
import clientAPIV3 from '@/clientAPIV3.ts'

export const CHAT_API = {
    getAnimationDetails: async (animationId?: number) => {
        const {data} = await axios.get<types.Animation>(`/animations/${animationId}`)
        return data
    },
    takeOver: async ({channelId}: {channelId: string}) => {
        const {data} = await axios.post(`/channels/${channelId}/take-over`)
        return data
    }
}

// We will use new Client API for refactored chat so axios will be imported from there
// Need to add later API for group conversations and broadcast
// All what is missed will be added when we will need it

// === Channel operations ===

export type GetChatChannelsParams = {
    channelStatus: ChannelsTypesE
    representedCreatorId?: string
    page?: number
    limit: number
    search?: string
}

export enum ChannelStatusToUpdateE {
    RESUME = 'resume',
    PAUSE = 'pause'
}

export const getChatChannels = async (params: GetChatChannelsParams): Promise<ListChannelsResponse> => {
    const {data} = await clientAPIV3.get(`/chats`, {params})
    return data
}

export const getChannelDetails = async (
    channelId: string,
    params: {representedCreatorId: string}
): Promise<Channel> => {
    const {data} = await clientAPIV3.get(`/chats/${channelId}`, {params})
    return data
}

export const updateChannelStatus = async (
    channelId: string,
    status: ChannelStatusToUpdateE,
    representedUserId?: number | string
): Promise<Channel> => {
    const params = representedUserId !== undefined ? {representedCreatorId: representedUserId} : undefined

    const {data} = await clientAPIV3.post(`/chats/${channelId}`, {status}, {params})

    return data
}

// === Message operations ===

export enum FileTypeE {
    PHOTO = 'photo',
    VIDEO = 'video',
    AUDIO = 'audio',
    DOCUMENT = 'document'
}

export enum ScopeUploadFileE {
    PHOTO = 'photo',
    VIDEO = 'video',
    CHAT_ATTACHMENT = 'chat_attachment',
    SURPRISE = 'surprise',
    INDENTITY_DOCUMENT = 'identity_document',
    DOCUMENT = 'document',
    GROUPS = 'groups',
    AUDIO = 'audio',
    MEDIA_PACKAGE = 'media_package',
    GOAL_MEDIA = 'goal_media',
    GOAL_PROOF = 'goal_proof'
}

export type ListMessagesParams = {
    page?: number
    limit?: number
    listDirection?: string
}

export type SendFilePayload = {
    fileName: string
    fileContentType: string
    fileType: FileTypeE
    scope: ScopeUploadFileE
}

type UnreadMessagesCountItem = {
    creatorId: number
    unreadMessagesCount: number
}

type UnreadMessagesResponse = {
    unreadMessagesCount: UnreadMessagesCountItem[]
}

export const getMessages = async (channelId: string, params: ListMessagesParams): Promise<ChatMessageList> => {
    const {data} = await clientAPIV3.get(`/chats/${channelId}/messages`, {params})
    return data
}

export const sendMessage = async (
    payload: SendMessageRequest,
    channelId?: string,
    representedCreatorId?: number
): Promise<{remainingDailyMessagesCount: number}> => {
    const params = representedCreatorId !== undefined ? {representedCreatorId} : undefined

    const {data} = await clientAPIV3.post(`/messages`, {...payload, chatIdentifier: channelId}, {params})

    return data
}

export const getUnreadMessageCountRepresentative = async (): Promise<UnreadMessagesResponse> => {
    const {data} = await clientAPIV3.get(`/chats/unread-messages-count/representatives`)
    return data
}

export const getUnreadMessageCount = async (representedCreatorId?: number): Promise<{unreadMessagesCount: number}> => {
    const {data} = await clientAPIV3.get(`/chats/unread-messages-count`, {params: {representedCreatorId}})
    return data
}

export const removeMessage = async ({channelId, messageId}: {channelId: string; messageId: string}): Promise<void> => {
    await clientAPIV3.delete(`/chats/${channelId}/messages/${messageId}`)
}

export const getChatReportCategories = async () => {
    const {data} = await clientAPIV3.get(`/chats/complaints/categories`)
    return data
}

export const reportMessage = async (
    channelId: string,
    payload: {
        messageId: number
        typeId: number
        userReported: number
        notes: string
    }
): Promise<{message: string}> => {
    const {data} = await clientAPIV3.post(`/chats/${channelId}/complaints`, payload)
    return data
}

export const markAllMessagesAsRead = async (
    chatIdentifier: string,
    representedCreatorId?: number | string
): Promise<{
    success: boolean
    unreadMessagesCleared: number
}> => {
    const params = representedCreatorId ? {representedCreatorId} : undefined

    const {data} = await clientAPIV3.post(`/chats/${chatIdentifier}/read`, undefined, {params})
    return data
}

export const sendChatFile = async (payload: SendFilePayload & {representedCreatorId?: number}) => {
    const {data} = await clientAPIV3.post(`/uploads`, payload)
    return data
}
/* ========== BROADCAST ========== */

export const createBroadcastGroup = async (
    payload: CreateBroadcastPayload,
    representedCreatorId?: number | string
): Promise<Broadcast> => {
    const params = representedCreatorId ? {representedCreatorId} : undefined

    const {data} = await clientAPIV3.post('/broadcasts', payload, {params})
    return data
}

export const getBroadcastMessages = async (broadcastGroupId: number): Promise<ListMessagesResponse> => {
    const {data} = await clientAPIV3.get(`/broadcasts/${broadcastGroupId}/messages`, {
        params: {responseType: 'extended'}
    })
    return data
}

export const sendBroadcastMessage = async (
    broadcastGroupId: string,
    payload: SendBroadcastMessageSchema,
    params?: Record<string, string | number>
): Promise<{remainingDailyMessagesCount: number}> => {
    const {data} = await clientAPIV3.post(
        `/broadcasts/${broadcastGroupId}/messages`,
        payload,
        params ? {params} : undefined
    )
    return data
}
/* ========== GROUP CONVERSATION ========== */

export type GetGroupsAndBroadcastsListingParams = {
    responseType: string
    representedCreatorId: number
    limit?: number
    page?: number
}

export const getGroupsAndBroadcastsListing = async (
    params: GetGroupsAndBroadcastsListingParams
): Promise<GetGroupsResponse> => {
    const {data} = await clientAPIV3.get('/groups', {params})
    return data
}

export const getGroupMessages = async (groupId: number, params: ListMessagesParams): Promise<ListMessagesResponse> => {
    const {data} = await clientAPIV3.get(`/groups/${groupId}/messages`, {params})
    return data
}

export const sendGroupMessage = async ({
    groupId,
    payload,
    representedCreatorId
}: {
    groupId: string
    payload: CreateGroupPayload
    representedCreatorId?: number | string
}) => {
    const params = representedCreatorId ? {representedCreatorId} : undefined

    const {data} = await clientAPIV3.post(`/groups/${groupId}/messages`, payload, {params})
    return data
}

export const createChatWithoutMessage = async (followerId: number): Promise<Channel> => {
    const {data} = await clientAPIV3.post(`/chats/followers/${followerId}`)
    return data
}

export const unlockTextMessageV3 = async (id?: number | null) => {
    const {data} = await clientAPIV3.post(`/paid-messages/${id}/unlock`)
    return data
}

export const unlockMediaMessageV3 = async (channelID: string, attachmentId: number) => {
    const {data} = await clientAPIV3.post(`chats/${channelID}/attachments/${attachmentId}/unlock`)
    return data
}

export const getMessageLanguage = async (text: string) => {
    const {data} = await clientAPIV3.post<types.MessageLanguageResponse>('/translations/language', {text})
    return data
}

export const getChatAttachmentById = async (chatIdentifier: string, chatAttachment: string) => {
    const {data} = await clientAPIV3.get(`/chats/${chatIdentifier}/attachments/${chatAttachment}`)
    return data
}
